import { Route, CanMatchFn } from '@angular/router';
import { inject } from '@angular/core';
import {
  redirectDashboardIfAuthenticated,
  redirectLoginIfNotAuthenticated,
  requirePermission,
  requireRole,
  requireClientSelection
} from '@kanek/shared-data-access';
import { GamePluginRegistry } from '@kanektech.com/game-engine';
import { canDeactivateCampaignEditor } from './features/campaigns/campaign-editor/campaign-editor.guard';

/** Route guard: only activates if the given game-engine plugin is registered. */
function requirePlugin(pluginId: string): CanMatchFn {
  return () => inject(GamePluginRegistry).hasPlugin(pluginId);
}

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'login',
    loadComponent: () => import('./features/login/login.component'),
    canActivate: [redirectDashboardIfAuthenticated()],
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./features/forgot-password/forgot-password.component'),
    canActivate: [redirectDashboardIfAuthenticated()],
  },
  {
    // Landing page for Firebase password-reset and invitation email links.
    // Must NOT have an auth guard – users arrive here unauthenticated.
    // Firebase appends: ?mode=resetPassword&oobCode=<code>&apiKey=<key>
    path: 'auth/complete-setup',
    loadComponent: () =>
      import('./features/auth/complete-setup/complete-setup.component'),
  },
  {
    path: 'select-client',
    loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.ClientSelectionComponent })),
    canActivate: [redirectLoginIfNotAuthenticated()],
  },
  {
    path: '',
    loadComponent: () => import('../app/features/layout/layout.component'),
    canActivate: [redirectLoginIfNotAuthenticated(), requireClientSelection()],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('../app/features/dashboard/dashboard.component'),
        canMatch: [requirePermission('campaigns', 'read')],
      },
      {
        path: 'campaigns',
        loadComponent: () => import('../app/features/campaigns/campaign-list.component'),
        canMatch: [requirePermission('campaigns', 'read')],
      },
      {
        path: 'campaigns/new',
        loadComponent: () => import('../app/features/campaigns/campaign-editor/campaign-editor.component'),
        canMatch: [requirePermission('campaigns', 'manage')],
        canDeactivate: [canDeactivateCampaignEditor],
      },
      {
        path: 'campaigns/:id/edit',
        loadComponent: () => import('../app/features/campaigns/campaign-editor/campaign-editor.component'),
        canMatch: [requirePermission('campaigns', 'manage')],
        canDeactivate: [canDeactivateCampaignEditor],
      },
      {
        path: 'prize-templates',
        loadComponent: () => import('../app/features/campaigns/prize-management/prize-template-management/prize-template-management.component')
          .then(m => ({ default: m.PrizeTemplateManagementComponent })),
        canMatch: [requirePermission('campaigns', 'manage')],
      },
      {
        path: 'poll-library',
        loadComponent: () => import('../app/features/poll-library/poll-library.component'),
        canMatch: [requirePermission('campaigns', 'manage'), requirePlugin('poll')],
      },
      {
        path: 'qr-management',
        loadComponent: () => import('../app/features/qr-management/qr-management.component'),
        canMatch: [requirePermission('campaigns', 'manage')],
      },
      {
        path: 'qr-demo',
        loadComponent: () => import('../app/features/qr-demo/qr-demo.component').then(m => ({ default: m.QRDemoComponent })),
        canMatch: [requireRole('super_admin')], // Only for super admins to test
      },
      {
        path: 'ar-marker-generator',
        loadComponent: () => import('../app/features/ar-marker-generator/ar-marker-generator.component').then(m => ({ default: m.ArMarkerGeneratorComponent })),
        canMatch: [requireRole('super_admin')], // Only for super admins - developer tool
      },
      {
        path: 'sports-kick-demo',
        loadComponent: () => import('../app/features/sports-kick-demo/sports-kick-demo.component').then(m => ({ default: m.SportsKickDemoComponent })),
        canMatch: [requireRole('super_admin')], // Developer sandbox — sports physics port
      },
      {
        path: 'heat-map',
        loadComponent: () => import('../app/features/campaigns/heat-map.component'),
        canMatch: [requireRole('super_admin')], // Incomplete - Super admin only
      },
      {
        path: 'advanced-campaigns',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.AdvancedCampaignManagementComponent })),
        canMatch: [requireRole('super_admin')], // Incomplete - Super admin only
      },
      {
        path: 'advanced-analytics',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.AdvancedSessionAnalyticsComponent })),
        canMatch: [requireRole('super_admin')], // Incomplete - Super admin only
      },
      {
        path: 'collaboration',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.RealTimeCollaborationComponent })),
        canMatch: [requireRole('super_admin')], // Incomplete - Super admin only
      },
      {
        path: 'account',
        loadComponent: () => import('./features/account/account-info.component'),
      },
      {
        path: 'authorize-user',
        loadComponent: () => import('../app/features/account/authorize-user.component'),
        canMatch: [requirePermission('users', 'manage')],
      },
      {
        path: 'user-management',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.UserManagementComponent })),
        canMatch: [requirePermission('users', 'manage')],
      },
      {
        path: 'client-onboarding',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.ClientOnboardingComponent })),
        canMatch: [requireRole('super_admin')],
      },
      {
        path: 'client-management',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.ClientManagementComponent })),
        canMatch: [requireRole('super_admin')],
      },
      {
        path: 'sessions',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.SessionManagementComponent })),
        canMatch: [requirePermission('sessions', 'read')],
      },
      {
        path: 'trash',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.TrashManagementComponent })),
        canMatch: [requirePermission('sessions', 'manage')],
      },
      {
        path: 'session-analytics',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.SessionAnalyticsComponent })),
        canMatch: [requireRole('super_admin')], // Incomplete - Super admin only
      },
      {
        path: 'notifications',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.NotificationsManagementComponent })),
        canMatch: [requirePermission('notifications', 'read')],
      },
      {
        path: 'db-scripts',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.DbScriptsComponent })),
        canMatch: [requireRole('super_admin')],
      },
      {
        path: 'messaging-settings',
        loadComponent: () => import('@kanek/portal-feature').then(m => ({ default: m.MessagingSettingsComponent })),
        canMatch: [requirePermission('campaigns', 'manage')],
      },
      {
        path: 'components',
        loadChildren: () => import('./features/ui-demo/ui-demo.routes').then(m => m.uiDemoRoutes),
        canMatch: [requireRole('super_admin')],
      },
      {

        path: 'experience-preview',
        loadComponent: () => import('../app/features/experience-preview/experience-preview-route.component').then(m => ({ default: m.ExperiencePreviewRouteComponent })),
        canMatch: [requireRole('super_admin')],
      },
      {
        path: 'experience-studio',
        loadChildren: () => import('../app/features/experience-studio/experience-studio.routes').then(m => m.experienceStudioRoutes),
        canMatch: [requirePermission('campaigns', 'manage')],
      },
      {
        path: 'unauthorized',
        loadComponent: () => import('./features/unauthorized/unauthorized.component'),
      },
    ],
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./features/unauthorized/unauthorized.component'),
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: 'unauthorized',
  },
];
